import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import type { PatientDetail, PatientHistoryEvent, PatientRecord, RecordedDonor, RecordedValue } from '@/api/patientsApi'
import type { PatientLisResult } from '@/api/lisApi'
import type { CreatedProduct } from '@/types/products'
import { catalogName, divisionOfCode, doseCards, doseSummary, inventoryState } from '@/lib/productRecord'
import {
  collectedAt,
  filterLis,
  flaggedCount,
  flagOf,
  historyWithResults,
  lastReceived,
  lisChips,
  peripheralCd34,
  resultStatus,
  type FlagTone,
  type LisFilter,
} from '@/lib/patientLis'
import { formatAge, formatDate as formatPatientDate, formatSex, patientNameParts } from '@/lib/patient'
import { productLocation } from '@/lib/shipping'
import { useLocationText } from '@/hooks/useStoragePlaces'
import { cn } from '@/lib/utils'
import {
  Card,
  CapsHeading,
  DinLink,
  Fact,
  formatClock,
  formatDate,
  formatDateTime,
  formatLongDate,
  INK,
  MUTED,
  NotRecorded,
  QUIET,
  RecordCard,
  RecordTable,
  SectionCard,
  Td,
  TonePill,
  usePrintMode,
} from '@/components/record/RecordParts'

/**
 * The sections of the patient's full record (z8nrz7g57g), drawn to the approved
 * mock. Each reads what is recorded — the patient's forms (by Field Library
 * system name), products, laboratory results and audits — and says "Not
 * recorded" where nothing is.
 */

export const PATIENT_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'history', label: 'History' },
  { id: 'procedures', label: 'Transplants & procedures' },
  { id: 'products', label: 'Products (DIN)' },
  { id: 'lis', label: 'LIS results' },
  { id: 'donors', label: 'Donors' },
  { id: 'storage', label: 'Storage' },
] as const

/**
 * The section the reader is in: the last one whose top has passed under the
 * bar, or — scrolled to the bottom, where short sections can never reach the
 * bar — the last one on screen. A clicked link wins while the page scrolls to it.
 */
function useActiveSection(ids: readonly string[], ready: boolean): [string, (id: string) => void] {
  const [active, setActive] = useState(ids[0])
  const lockedUntil = useRef(0)
  useEffect(() => {
    const onScroll = (e: Event) => {
      if (Date.now() < lockedUntil.current) return
      const tops = ids.map((id) => [id, document.getElementById(id)?.getBoundingClientRect().top] as const)
      let current = ids[0]
      for (const [id, top] of tops) if (top != null && top <= 96) current = id
      const scroller = e.target instanceof HTMLElement ? e.target : document.scrollingElement
      if (scroller && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) {
        for (const [id, top] of tops) if (top != null && top < window.innerHeight - 120) current = id
      }
      setActive(current)
    }
    document.addEventListener('scroll', onScroll, true)
    return () => document.removeEventListener('scroll', onScroll, true)
    // `ready`: the sections mount after the patient loads.
  }, [ids, ready])
  const pick = (id: string) => {
    lockedUntil.current = Date.now() + 1200
    setActive(id)
  }
  return [active, pick]
}

/** True while the element is on screen (the compact identity shows once the header scrolls away). */
function useOnScreen(ref: RefObject<HTMLElement | null>): boolean {
  const [on, setOn] = useState(true)
  useEffect(() => {
    if (!ref.current) return
    const observer = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { rootMargin: '-56px 0px 0px 0px' })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref])
  return on
}

/**
 * The section links, sticky at the top while the page scrolls. Once the
 * patient header has scrolled away, the bar carries who this is, so the
 * record never loses its identity.
 */
export function SectionNav({
  headerRef,
  initials,
  name,
  mrn,
  ready,
}: {
  headerRef: RefObject<HTMLElement | null>
  initials: string
  name: string
  mrn: string
  /** The sections are on the page (the patient has loaded). */
  ready: boolean
}) {
  const ids = useMemo(() => PATIENT_SECTIONS.map((s) => s.id), [])
  const [active, pick] = useActiveSection(ids, ready)
  const headerOnScreen = useOnScreen(headerRef)
  return (
    <nav
      aria-label='Patient sections'
      className='sticky top-0 z-30 border-b border-[#E4E7EC] bg-white px-6 shadow-[0_1px_2px_rgba(16,24,40,0.04)] print:hidden'
    >
      <div className='mx-auto flex max-w-[1392px] items-center gap-x-6 overflow-x-auto [scrollbar-width:none]'>
        <div
          className={cn(
            'flex items-center gap-2 overflow-hidden transition-all duration-200',
            headerOnScreen ? 'w-0 opacity-0' : 'w-auto pr-2 opacity-100',
          )}
          aria-hidden={headerOnScreen}
        >
          <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#1F7BC6] text-[11px] font-bold text-white'>
            {initials}
          </span>
          <span className={cn('whitespace-nowrap text-sm font-semibold', INK)}>{name}</span>
          <span className={cn('whitespace-nowrap font-mono text-xs', MUTED)}>{mrn}</span>
        </div>
        {PATIENT_SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            onClick={(e) => {
              e.preventDefault()
              pick(s.id)
              document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            aria-current={active === s.id ? 'location' : undefined}
            className={cn(
              'flex-none whitespace-nowrap border-b-2 px-1 py-3 text-sm font-semibold no-underline transition-colors',
              active === s.id
                ? 'border-[#1F7BC6] text-[#175CD3]'
                : 'border-transparent text-[#344054] hover:border-[#175CD3] hover:text-[#175CD3]',
            )}
          >
            {s.label}
          </a>
        ))}
      </div>
    </nav>
  )
}

/** "Nov 5, 2026 · in 7 days" for a planned infusion date, or the value as written. */
export function dayZeroText(v: RecordedValue | undefined, now: number = Date.now()): string | null {
  if (!v) return null
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(v.value)?.[1]
  if (!iso) return v.value
  const days = Math.round((Date.parse(`${iso}T12:00:00`) - now) / 86_400_000)
  const when = days === 0 ? 'today' : days > 0 ? `in ${days} day${days === 1 ? '' : 's'}` : `${-days} day${days === -1 ? '' : 's'} ago`
  return `${formatLongDate(iso)} · ${when}`
}

/** A recorded value, with the form it came from on hover. */
function Recorded({ v, render }: { v: RecordedValue | undefined; render?: (value: string) => ReactNode }) {
  if (!v) return null
  return <span title={`From ${v.formName}, ${formatDateTime(v.recordedAt) ?? ''}`}>{render ? render(v.value) : v.value}</span>
}

/**
 * Who the patient is. Born and Age stay separate rows with the formats the
 * DOB smoke test reads ("Jan 1, 2000"; "45 years, 5 months"). Fields the EHR
 * may send (middle name, contact, address) show only when they are on file,
 * so nothing the old card showed is lost.
 */
export function IdentityCard({
  patient,
  mrn,
  isError = false,
}: {
  patient: PatientDetail | undefined
  mrn: string
  /** The patient's details failed to load — not an MRN with no Patient row, which is no error. */
  isError?: boolean
}) {
  if (isError) {
    return (
      <RecordCard title='Identity' narrowLabels>
        <Fact label='MRN'>
          <span className='font-mono text-[12.5px]'>{mrn}</span>
        </Fact>
        <div className='col-span-2'>
          <LoadFailed what="The patient's details" />
        </div>
      </RecordCard>
    )
  }
  const { first, last } = patient ? patientNameParts(patient) : { first: null, last: null }
  const address = patient
    ? [patient.addressLine, patient.city, patient.state, patient.postalCode, patient.country].filter(Boolean).join(', ')
    : ''
  return (
    <RecordCard title='Identity' narrowLabels>
      <Fact label='First name'>{first}</Fact>
      {patient?.middleName && <Fact label='Middle name'>{patient.middleName}</Fact>}
      <Fact label='Last name'>{last}</Fact>
      <Fact label='MRN'>
        <span className='font-mono text-[12.5px]'>{mrn}</span>
      </Fact>
      <Fact label='Born'>{formatPatientDate(patient?.birthDate)}</Fact>
      <Fact label='Age'>{formatAge(patient?.birthDate)}</Fact>
      <Fact label='Sex'>{formatSex(patient?.sex)}</Fact>
      <Fact label='Language'>{patient?.preferredLanguage}</Fact>
      <Fact label='Account no.'>{patient?.accountNumber}</Fact>
      {patient?.phone && <Fact label='Phone'>{patient.phone}</Fact>}
      {patient?.email && <Fact label='Email'>{patient.email}</Fact>}
      {address && <Fact label='Address'>{address}</Fact>}
    </RecordCard>
  )
}

/** Said where a section's data failed to load, so a failure never reads as "nothing recorded". */
function LoadFailed({ what }: { what: string }) {
  return <p className={cn('m-0 text-sm', MUTED)}>{what} could not be loaded. Refresh the page to try again.</p>
}

export function ClinicalCard({ record, isError = false }: { record: PatientRecord | undefined; isError?: boolean }) {
  if (isError) {
    return (
      <Card className='flex flex-col gap-3.5 p-5'>
        <CapsHeading>Clinical</CapsHeading>
        <LoadFailed what='The clinical record' />
      </Card>
    )
  }
  const c = record?.clinical ?? {}
  const weight = c.recipient_weight_kg ?? c.last_weight_kg
  return (
    <RecordCard title='Clinical' narrowLabels>
      <Fact label='Diagnosis'>
        {c.diagnosis || c.icd_code ? (
          <>
            <Recorded v={c.diagnosis} />
            {c.icd_code && <span className={cn('ml-1.5 font-mono text-xs', MUTED)}>{c.icd_code.value}</span>}
          </>
        ) : null}
      </Fact>
      <Fact label='Treatment plan'>
        {c.transplant_type || c.protocol ? (
          <>
            <Recorded v={c.transplant_type} />
            {c.protocol && <span className={cn('block text-[13px]', MUTED)}>Protocol {c.protocol.value}</span>}
          </>
        ) : null}
      </Fact>
      <Fact label='Attending'>{c.transplant_physician && <Recorded v={c.transplant_physician} />}</Fact>
      <Fact label='ABO / Rh'>{c.recipient_abo_rh && <Recorded v={c.recipient_abo_rh} />}</Fact>
      <Fact label='Weight'>
        {weight && <Recorded v={weight} render={(w) => `${w} kg (${formatDate(weight.recordedAt)})`} />}
      </Fact>
      <Fact label='CMV'>{c.cmv_status && <Recorded v={c.cmv_status} />}</Fact>
      {c.preparative_regimen && (
        <Fact label='Conditioning'>
          <Recorded v={c.preparative_regimen} />
        </Fact>
      )}
      <Fact label='HLA report'>
        {c.hla_report_attached && (
          <Recorded v={c.hla_report_attached} render={(v) => (/^(yes|true)$/i.test(v) ? 'Attached · see LIS results' : v)} />
        )}
      </Fact>
    </RecordCard>
  )
}

export function AtAGlance({
  products,
  lis,
  record,
}: {
  /** Undefined when they could not be loaded: the tiles say "—", never 0. */
  products: CreatedProduct[] | undefined
  lis: PatientLisResult[] | undefined
  record: PatientRecord | undefined
}) {
  const inStorage = (products ?? []).filter((p) => p.storageNodeId && !p.supersededById)
  // The newest stored product with a CD34+ dose: what a coordinator checks first.
  const dosed = inStorage.map((p) => ({ p, cd34: doseCards(p.limsValues)[1].value })).find((x) => x.cd34 != null)
  const received = lastReceived(lis ?? [])
  const division = dosed ? divisionOfCode(dosed.p.productDescriptionCode) : null
  const tile = (value: string | number, caption: string) => (
    <div className='rounded-lg bg-[#F4F6F9] p-3'>
      <div className='text-[26px] font-bold leading-tight tabular-nums'>{value}</div>
      <div className={cn('text-[13px]', MUTED)}>{caption}</div>
    </div>
  )
  return (
    <Card className='flex flex-col gap-3 p-5'>
      <CapsHeading>At a glance</CapsHeading>
      <div className='grid grid-cols-2 gap-3'>
        {tile(products ? inStorage.length : '—', inStorage.length === 1 ? 'Product in storage' : 'Products in storage')}
        {tile(dosed?.cd34 ?? '—', dosed ? `CD34+ ×10⁶/kg${division ? ` (${division})` : ''}` : 'No CD34+ dose recorded')}
        {tile(lis ? lis.length : '—', lis ? `LIS panels · ${flaggedCount(lis)} flagged results` : 'LIS results not available')}
        {tile(record?.donors.length ?? '—', record?.donors.length === 1 ? 'Linked donor' : 'Linked donors')}
      </div>
      <div className={cn('text-[13px]', MUTED)}>
        {received ? `Last LIS result received ${formatDateTime(received)}` : 'No LIS results received yet'}
      </div>
    </Card>
  )
}

const DOT: Record<PatientHistoryEvent['state'], string> = {
  planned: 'box-border border-[2.5px] border-[#1F7BC6] bg-white',
  in_progress: 'bg-[#F79009]',
  done: 'bg-[#12B76A]',
}
const TAG: Record<PatientHistoryEvent['state'], { label: string; tone: 'blue' | 'amber' | 'green' }> = {
  planned: { label: 'Planned', tone: 'blue' },
  in_progress: { label: 'In progress', tone: 'amber' },
  done: { label: 'Done', tone: 'green' },
}

const HISTORY_SHOWN = 12

export function HistoryTimeline({
  record,
  lis,
  lisPending = false,
  lisError = false,
  isLoading,
  isError = false,
}: {
  record: PatientRecord | undefined
  /** The laboratory results the page loaded for its LIS section, added to the history here. */
  lis: PatientLisResult[] | undefined
  /** The results are still loading, or failed: the history says it is without them. */
  lisPending?: boolean
  lisError?: boolean
  isLoading: boolean
  isError?: boolean
}) {
  const [all, setAll] = useState(false)
  const print = usePrintMode()
  const events = useMemo(() => historyWithResults(record?.history ?? [], lis), [record?.history, lis])
  const shown = all || print ? events : events.slice(0, HISTORY_SHOWN)
  return (
    <Card id='history' className='flex flex-col gap-4 px-6 py-5'>
      <div className='flex flex-wrap items-baseline justify-between gap-2'>
        <h2 className={cn('m-0 text-xl font-bold', INK)}>Patient history</h2>
        <div className={cn('text-[13px]', MUTED)}>Newest first · planned events shown in blue</div>
      </div>
      {!isLoading && !isError && (lisPending || lisError) && (
        <p className={cn('m-0 text-[13px]', MUTED)}>
          {lisPending
            ? 'Laboratory results are still loading and are not in this history yet.'
            : 'Laboratory results could not be loaded, so they are not in this history.'}
        </p>
      )}
      {isLoading ? (
        <p className={cn('m-0 text-sm', MUTED)}>Loading…</p>
      ) : isError ? (
        <LoadFailed what='The patient history' />
      ) : events.length === 0 ? (
        <p className={cn('m-0 text-sm', MUTED)}>Nothing recorded for this patient yet.</p>
      ) : (
        <>
          <ol className='m-0 flex list-none flex-col p-0'>
            {shown.map((e, i) => {
              const tag = TAG[e.state]
              return (
                <li
                  key={`${e.at}-${i}`}
                  className='grid grid-cols-[150px_24px_minmax(0,1fr)] gap-3 py-2.5 max-sm:grid-cols-[96px_20px_minmax(0,1fr)]'
                >
                  <div className='flex flex-col gap-0.5'>
                    <div className='text-sm font-semibold'>{formatLongDate(e.at)}</div>
                    <div className={cn('text-[12.5px]', MUTED)}>
                      {e.dateOnly
                        ? e.state === 'planned'
                          ? 'planned'
                          : '—'
                        : `${formatClock(e.at)}${e.state === 'planned' ? ' · planned' : ''}`}
                    </div>
                  </div>
                  <div className='flex justify-center pt-1'>
                    <span className={cn('h-3 w-3 rounded-full', DOT[e.state])} aria-hidden='true' />
                  </div>
                  <div className='flex min-w-0 flex-col gap-[3px]'>
                    <div className='text-[14.5px] font-semibold'>
                      {e.title}{' '}
                      <TonePill tone={tag.tone} className='ml-1.5'>
                        {tag.label}
                      </TonePill>
                    </div>
                    {(e.detail || (e.productId && e.din)) && (
                      <div className={cn('text-[13.5px]', MUTED)}>
                        {e.detail}
                        {e.productId && e.din && (
                          <>
                            {e.detail ? ': ' : ''}
                            <DinLink productId={e.productId} din={e.din} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
          {events.length > HISTORY_SHOWN && !print && (
            <button
              type='button'
              className='self-start text-sm font-semibold text-[#175CD3] hover:underline'
              onClick={() => setAll((v) => !v)}
            >
              {all ? 'Show fewer' : `Show all ${events.length} events`}
            </button>
          )}
        </>
      )}
    </Card>
  )
}

/** The products each run made, for the procedures table. */
export function productsByRun(products: CreatedProduct[]): Map<string, CreatedProduct[]> {
  const out = new Map<string, CreatedProduct[]>()
  for (const p of products) {
    if (!p.procedureId) continue
    out.set(p.procedureId, [...(out.get(p.procedureId) ?? []), p])
  }
  return out
}

/** How many of a run's DINs its row shows on screen. */
const RUN_DINS_SHOWN = 3

/** The run's products: one DIN link per DIN, with its divisions ("A0, B0"). */
export function RunProducts({ products }: { products: CreatedProduct[] | undefined }) {
  const print = usePrintMode()
  if (!products?.length) return <span className={QUIET}>—</span>
  const byDin = new Map<string, CreatedProduct[]>()
  for (const p of products) byDin.set(p.din, [...(byDin.get(p.din) ?? []), p])
  const groups = [...byDin.values()]
  // Three on screen (the Products section lists them all); every one on paper.
  const shown = print ? groups : groups.slice(0, RUN_DINS_SHOWN)
  return (
    <span className='flex flex-col gap-0.5'>
      {shown.map((group) => {
        const divisions = group.map((p) => divisionOfCode(p.productDescriptionCode)).filter(Boolean)
        const first = group.find((p) => divisionOfCode(p.productDescriptionCode)) ?? group[0]
        return (
          // The row around this opens the procedure on click and Enter; the DIN opens the product.
          <span key={first.id} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <DinLink productId={first.id} din={first.din} />
            <span className={cn('ml-1', MUTED)}>{divisions.length ? divisions.join(', ') : 'whole collection'}</span>
          </span>
        )
      })}
      {groups.length > shown.length && <span className={QUIET}>+{groups.length - shown.length} more</span>}
    </span>
  )
}

function donorTitle(d: RecordedDonor): string {
  const type = d.values.donor_type ?? ''
  if (/autolog|self/i.test(type) || /^self$/i.test(d.values.relationship ?? '')) return 'Self (autologous)'
  return type ? `${type} donor` : 'Donor'
}

/** The donor recorded for a run, as a short line. */
export function runDonor(donors: RecordedDonor[], procedureId: string): string | null {
  const d = donors.find((x) => x.procedureIds.includes(procedureId))
  if (!d) return null
  if (donorTitle(d) === 'Self (autologous)') return 'Self'
  const name = [d.values.donor_first_name, d.values.donor_last_name].filter(Boolean).join(' ')
  return name ? `${name}${d.values.relationship ? ` (${d.values.relationship.toLowerCase()})` : ''}` : donorTitle(d)
}

export function ProductsSection({
  products,
  isLoading,
  isError = false,
  donors,
}: {
  products: CreatedProduct[]
  isLoading: boolean
  isError?: boolean
  donors: RecordedDonor[]
}) {
  const locate = useLocationText()
  return (
    <SectionCard
      id='products'
      title='Products (DIN)'
      subtitle='Every product collected for or given to this patient. Click a DIN for its full record and LIS data.'
      flush
    >
      {isLoading ? (
        <p className={cn('m-0 px-6 pb-5 text-sm', MUTED)}>Loading…</p>
      ) : isError ? (
        <div className='px-6 pb-5'>
          <LoadFailed what="The patient's products" />
        </div>
      ) : products.length === 0 ? (
        <p className={cn('m-0 px-6 pb-5 text-sm', MUTED)}>No products made for this patient yet.</p>
      ) : (
        <RecordTable head={['DIN', 'Product', 'Div.', 'Volume', 'Created', 'Donor', 'Dose /kg', 'Location', 'Status']}>
          {products.map((p) => {
            const state = inventoryState(p)
            const where = productLocation(p)
            return (
              <tr key={p.id} className={cn(p.supersededById && 'opacity-60')}>
                <Td>
                  <DinLink productId={p.id} din={p.din} />
                </Td>
                <Td>{catalogName(p.catalogDescription) ?? <span className='font-mono text-xs'>{p.productDescriptionCode}</span>}</Td>
                <Td>{divisionOfCode(p.productDescriptionCode) ?? '—'}</Td>
                <Td className='whitespace-nowrap tabular-nums'>{p.volumeMl != null ? `${p.volumeMl} mL` : '—'}</Td>
                <Td className='whitespace-nowrap'>{formatDateTime(p.createdAt)}</Td>
                <Td>{(p.procedureId && runDonor(donors, p.procedureId)) ?? '—'}</Td>
                <Td>{doseSummary(p.limsValues) ?? '—'}</Td>
                <Td>
                  {where.kind === 'stored' ? (
                    locate(where.text)
                  ) : where.kind === 'shipped' ? (
                    where.text
                  ) : (
                    // A divided bag became its divisions: there is nothing left to place.
                    <span className={QUIET}>{state.label === 'Divided' ? '—' : 'Not placed'}</span>
                  )}
                </Td>
                <Td>
                  <TonePill tone={state.tone}>{state.label}</TonePill>
                </Td>
              </tr>
            )
          })}
        </RecordTable>
      )}
    </SectionCard>
  )
}

const FLAG_CLASS: Record<FlagTone, string> = {
  high: 'font-bold text-[#B42318]',
  low: 'font-bold text-[#B54708]',
  normal: 'font-semibold text-[#05603A]',
  pending: 'font-bold text-[#B54708]',
  none: QUIET,
}

export function LisSection({
  results,
  isLoading,
  isError,
}: {
  results: PatientLisResult[] | undefined
  isLoading: boolean
  isError: boolean
}) {
  const [filter, setFilter] = useState<LisFilter>('all')
  const print = usePrintMode()
  const all = useMemo(() => results ?? [], [results])
  const chips = useMemo(() => lisChips(all), [all])
  const shown = useMemo(() => filterLis(all, filter), [all, filter])
  const cd34 = useMemo(() => peripheralCd34(all), [all])
  const showTrend = (filter === 'all' || filter === 'hematology' || filter === 'patient') && cd34.points.length > 0
  const received = lastReceived(all)
  const valueCount = all.reduce((n, r) => n + r.observations.length, 0)

  return (
    <Card id='lis' className='flex flex-col gap-[18px] px-6 py-5'>
      <div className='flex flex-wrap items-baseline justify-between gap-2'>
        <div className='flex flex-col gap-1'>
          <h2 className={cn('m-0 text-xl font-bold', INK)}>LIS results</h2>
          <div className={cn('text-[13px]', MUTED)}>All results for the patient and for every linked product, from the LIS interface</div>
        </div>
        {all.length > 0 && (
          <div className={cn('text-[13px]', MUTED)}>
            {received && `Last received ${formatDateTime(received)} · `}
            {all.length} {all.length === 1 ? 'panel' : 'panels'} · {valueCount} results
          </div>
        )}
      </div>
      {isLoading ? (
        <p className={cn('m-0 text-sm', MUTED)}>Loading laboratory results…</p>
      ) : isError ? (
        <p className={cn('m-0 text-sm', MUTED)}>LIS results could not be loaded. Viewing them needs access to integrations.</p>
      ) : all.length === 0 ? (
        <p className={cn('m-0 text-sm', MUTED)}>No LIS results for this patient yet.</p>
      ) : (
        <>
          <div role='group' aria-label='Filter laboratory results' className={cn('flex flex-wrap gap-2', print && 'hidden')}>
            {chips.map((c) => (
              <button
                key={c.filter}
                type='button'
                aria-pressed={filter === c.filter}
                onClick={() => setFilter(c.filter)}
                className={cn(
                  'min-h-10 cursor-pointer rounded-full border px-3.5 text-[13px] font-semibold transition-colors',
                  filter === c.filter
                    ? 'border-[#1F7BC6] bg-[#1F7BC6] text-white'
                    : 'border-[#D0D5DD] bg-white text-[#344054] hover:bg-[#F9FAFB]',
                )}
              >
                {c.label}
                <span className={cn('ml-1.5 text-xs', filter === c.filter ? 'text-white/80' : QUIET)}>{c.count}</span>
              </button>
            ))}
          </div>

          {showTrend && <Cd34Chart points={cd34.points} threshold={cd34.threshold} />}

          {shown.map((r) => {
            const status = resultStatus(r.resultStatus)
            return (
              <div key={r.id} className='record-panel overflow-hidden rounded-[10px] border border-[#E4E7EC]'>
                <div
                  className={cn(
                    'flex flex-wrap items-baseline gap-x-5 gap-y-2 border-b border-[#E4E7EC] bg-[#F9FAFB] px-4 py-3.5 text-[13px]',
                    MUTED,
                  )}
                >
                  <div className={cn('text-[15px] font-bold', INK)}>{r.testName ?? r.testCode}</div>
                  <div>
                    Specimen:{' '}
                    {r.productId && r.din ? (
                      <DinLink productId={r.productId} din={r.din} />
                    ) : r.din ? (
                      <span className='font-mono'>{r.din}</span>
                    ) : (
                      'Patient'
                    )}
                    {r.stage && ` · ${r.stage}`}
                  </div>
                  <div>Collected {formatDateTime(collectedAt(r))}</div>
                  {r.fillerOrderId && <div className='font-mono text-[12.5px]'>Acc. {r.fillerOrderId}</div>}
                  {status.label !== 'Final' && <TonePill tone={status.tone}>{status.label}</TonePill>}
                </div>
                <RecordTable head={['Test', 'Result', 'Units', 'Reference / spec', 'Flag', 'Status']}>
                  {r.observations.map((o) => {
                    const flag = flagOf(o, r.resultStatus)
                    const st = resultStatus(o.status ?? r.resultStatus)
                    return (
                      <tr key={o.id}>
                        <Td>{o.codeText ?? o.code}</Td>
                        <Td className='font-semibold'>{o.valueCodeText ?? o.value ?? '—'}</Td>
                        <Td>{o.units ?? '—'}</Td>
                        <Td>{o.referenceRange ?? '—'}</Td>
                        <Td className={FLAG_CLASS[flag.tone]}>{flag.label}</Td>
                        <Td>
                          <TonePill tone={st.tone}>{st.label}</TonePill>
                        </Td>
                      </tr>
                    )
                  })}
                </RecordTable>
              </div>
            )
          })}
        </>
      )}
    </Card>
  )
}

/** Peripheral blood CD34+ by day, with the apheresis threshold when the range states one. */
function Cd34Chart({ points, threshold }: { points: { at: string; value: number; units: string | null }[]; threshold: number | null }) {
  const W = 420
  const left = 40
  const right = 20
  const top = 24
  const base = 140
  const max = Math.max(...points.map((p) => p.value), threshold ?? 0) * 1.15 || 1
  const y = (v: number) => base - (v / max) * (base - top)
  const slot = (W - left - right) / points.length
  const bar = Math.min(48, slot * 0.6)
  const first = points[0]
  const last = points[points.length - 1]
  const passed = threshold != null ? points.find((p) => p.value >= threshold) : undefined
  const units = (first.units ?? 'cells/µL').replace(/uL/g, 'µL')
  const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
  return (
    <div className='flex flex-wrap items-center gap-6 rounded-[10px] border border-[#E4E7EC] px-5 py-4'>
      <div className='flex min-w-[16rem] flex-[1_1_260px] flex-col gap-1.5'>
        <div className='text-[15px] font-bold'>Peripheral blood CD34+ during mobilization</div>
        <div className={cn('text-[13.5px]', MUTED)}>
          {points.length > 1
            ? `Rose from ${first.value} to ${last.value} ${units} over ${points.length} counts`
            : `${first.value} ${units} on ${day(first.at)}`}
          {threshold != null
            ? passed
              ? ` and passed the ${threshold} ${units} apheresis threshold on ${day(passed.at)}.`
              : `, still below the ${threshold} ${units} threshold.`
            : '.'}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} 170`}
        className='h-auto w-[420px] max-w-full'
        role='img'
        aria-label={`Peripheral blood CD34+: ${points.map((p) => `${day(p.at)} ${p.value}`).join(', ')}${threshold != null ? `; threshold ${threshold}` : ''}`}
      >
        <line x1={left} x2={W - right} y1={base} y2={base} stroke='#D0D5DD' strokeWidth={1} />
        {threshold != null && (
          <>
            <line x1={left} x2={W - right} y1={y(threshold)} y2={y(threshold)} stroke='#B54708' strokeWidth={1.5} strokeDasharray='5 4' />
            <text x={left + 4} y={y(threshold) - 8} fill='#B54708' style={{ font: '600 11px sans-serif' }}>
              Apheresis threshold {threshold}/µL
            </text>
          </>
        )}
        {points.map((p, i) => {
          const cx = left + slot * i + slot / 2
          const t = y(p.value)
          return (
            <g key={p.at}>
              <title>{`${day(p.at)}: ${p.value} ${units}`}</title>
              <rect x={cx - bar / 2} y={t} width={bar} height={Math.max(base - t, 1)} rx={3} fill='#1F7BC6' />
              <text x={cx} y={t - 6} textAnchor='middle' fill='#1D2939' style={{ font: '700 12px sans-serif' }}>
                {p.value}
              </text>
              <text x={cx} y={base + 18} textAnchor='middle' fill='#475467' style={{ font: '500 11px sans-serif' }}>
                {day(p.at)}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/** Age in whole years from a stored date ("48"), or null. */
function ageOf(date: string | undefined): string | null {
  const iso = date ? /^(\d{4})-(\d{2})-(\d{2})/.exec(date) : null
  if (!iso) return null
  const born = new Date(`${iso[0]}T12:00:00`)
  const now = new Date()
  let age = now.getFullYear() - born.getFullYear()
  if (now < new Date(now.getFullYear(), born.getMonth(), born.getDate())) age -= 1
  return Number.isFinite(age) && age >= 0 ? String(age) : null
}

export function DonorsSection({
  record,
  isLoading,
  isError = false,
  procedureName,
  patient,
  products,
}: {
  record: PatientRecord | undefined
  isLoading: boolean
  isError?: boolean
  procedureName: (id: string) => string | null
  /** The patient: an autologous donor is them, which the donor form does not repeat. */
  patient: { name: string | null; mrn: string; age: number | null | undefined; sex: string | null; abo: string | null; weight: string | null }
  products: CreatedProduct[]
}) {
  const donors = record?.donors ?? []
  return (
    <section id='donors' className='flex scroll-mt-20 flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <h2 className={cn('m-0 text-xl font-bold', INK)}>Donors</h2>
        <div className={cn('text-[13px]', MUTED)}>Every donor linked to this patient’s transplants</div>
      </div>
      {isLoading ? (
        <p className={cn('m-0 text-sm', MUTED)}>Loading…</p>
      ) : isError ? (
        <Card className='px-5 py-4'>
          <LoadFailed what='The donors' />
        </Card>
      ) : donors.length === 0 ? (
        <Card className={cn('px-5 py-4 text-sm', MUTED)}>No donor recorded on this patient’s forms yet.</Card>
      ) : (
        <div className='grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(380px,1fr))] max-sm:grid-cols-1'>
          {donors.map((d) => {
            const v = d.values
            const self = donorTitle(d) === 'Self (autologous)'
            const name = [v.donor_first_name, v.donor_last_name].filter(Boolean).join(' ') || (self ? (patient.name ?? '') : '')
            const id = v.donor_mrn_nmdp_id ?? v.nmdp_donor_id ?? v.donor_cbu_id
            const runs = d.procedureIds.map(procedureName).filter((n): n is string => !!n)
            const collected = products.filter((p) => p.procedureId && d.procedureIds.includes(p.procedureId))
            const firstCollected = collected.map((p) => p.createdAt).sort()[0]
            const age = self ? (patient.age != null ? String(patient.age) : null) : ageOf(v.donor_date_of_birth)
            const sex = self ? patient.sex : v.donor_gender
            const byDin = new Map<string, CreatedProduct[]>()
            for (const p of collected) byDin.set(p.din, [...(byDin.get(p.din) ?? []), p])
            return (
              <Card key={d.key} className='flex flex-col gap-3.5 p-5'>
                <div className='flex flex-wrap items-center justify-between gap-3'>
                  <div className='text-[17px] font-bold'>{donorTitle(d)}</div>
                  {collected.length > 0 ? (
                    <TonePill tone='green'>Collected {formatDate(firstCollected)}</TonePill>
                  ) : (
                    <TonePill tone='gray'>{v.donor_eligibility ? `${v.donor_eligibility} · not collected` : 'Not collected'}</TonePill>
                  )}
                </div>
                <dl className={cn('m-0 grid grid-cols-[150px_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm', INK)}>
                  <Fact label='Donor'>
                    {name || id || self ? (
                      <>
                        {name}
                        {self ? (
                          <>
                            {' '}
                            · MRN <span className='font-mono text-[12.5px]'>{patient.mrn}</span>
                          </>
                        ) : (
                          id && (
                            <>
                              {name ? ' · ' : ''}Donor ID <span className='font-mono text-[12.5px]'>{id}</span>
                            </>
                          )
                        )}
                      </>
                    ) : null}
                  </Fact>
                  {!self && <Fact label='Relationship'>{v.relationship}</Fact>}
                  <Fact label='Age / sex'>{[age, sex].filter(Boolean).join(' · ')}</Fact>
                  <Fact label='ABO / Rh'>
                    {[v.donor_abo_rh ?? (self ? patient.abo : null), v.abo_incompatibility && `ABO incompatibility: ${v.abo_incompatibility}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </Fact>
                  {self && <Fact label='Weight'>{patient.weight}</Fact>}
                  <Fact label='Eligibility'>{v.donor_eligibility}</Fact>
                  {v.donor_antibody_screen && <Fact label='Antibody screen'>{v.donor_antibody_screen}</Fact>}
                  <Fact label='Infectious disease'>
                    {v.date_of_idm_results ? `Tested ${formatDate(v.date_of_idm_results) ?? v.date_of_idm_results}` : null}
                  </Fact>
                  <Fact label='Products'>
                    {byDin.size > 0 ? (
                      [...byDin.values()].map((group) => {
                        const first = group.find((p) => divisionOfCode(p.productDescriptionCode)) ?? group[0]
                        const divisions = group.map((p) => divisionOfCode(p.productDescriptionCode)).filter(Boolean)
                        return (
                          <span key={first.id} className='block'>
                            <DinLink productId={first.id} din={first.din} />
                            {divisions.length > 0 && ` · ${divisions.join(', ')}`}
                          </span>
                        )
                      })
                    ) : (
                      <NotRecorded>None collected</NotRecorded>
                    )}
                  </Fact>
                  <Fact label='Used in'>{runs.join(', ')}</Fact>
                </dl>
                <div className={cn('text-xs', QUIET)}>
                  From {d.formName}, {formatDate(d.recordedAt)}
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </section>
  )
}

export function StorageSection({
  products,
  record,
  isError = false,
}: {
  products: CreatedProduct[]
  record: PatientRecord | undefined
  /** The products could not be loaded. */
  isError?: boolean
}) {
  const locate = useLocationText()
  const stored = products.filter((p) => p.storagePath && !p.supersededById)
  return (
    <SectionCard id='storage' title='Storage' subtitle='Where each of this patient’s products is held now' flush>
      {isError ? (
        <div className='px-6 pb-5'>
          <LoadFailed what="The patient's storage" />
        </div>
      ) : stored.length === 0 ? (
        <p className={cn('m-0 px-6 pb-5 text-sm', MUTED)}>None of this patient’s products is in storage.</p>
      ) : (
        <RecordTable head={['DIN / container', 'Location', 'Device', 'Temperature', 'Placed']}>
          {stored.map((p) => {
            const division = divisionOfCode(p.productDescriptionCode)
            const place = record?.storage?.[p.id]
            const unit = place?.unit
            return (
              <tr key={p.id}>
                <Td>
                  <DinLink productId={p.id} din={p.din} />
                  <span className={MUTED}> · {division ? `${division} bag` : (catalogName(p.catalogDescription) ?? p.productDescriptionCode)}</span>
                </Td>
                <Td>{locate(p.storagePath!)}</Td>
                <Td>
                  {unit
                    ? [unit.equipmentLabel ?? unit.name ?? unit.code, unit.equipmentType].filter(Boolean).join(' · ')
                    : p.storagePath!.split('/')[0]}
                </Td>
                <Td>
                  {place?.condition ? [place.condition.label, place.condition.temperature].filter(Boolean).join(' · ') : <NotRecorded />}
                </Td>
                <Td className='whitespace-nowrap'>{formatDateTime(record?.storedAt[p.id]) ?? '—'}</Td>
              </tr>
            )
          })}
        </RecordTable>
      )}
    </SectionCard>
  )
}
